// iter155 — Rice Running Report ledger helpers.
//
// A "receipt" is a single-date rice receipt stored on a fund_balance
// document. A "consumption" row is a daily entry whose dayKind is working
// AND has non-zero attendance (built via `buildDayBreakdown`). No-MDM and
// holiday entries contribute ZERO rice to the ledger, by design.
//
// This module aggregates these two streams into daily / weekly / monthly /
// yearly running-balance series. Opening balance for the first date in
// range is derived from `cascadeClosing` of the previous month so the
// running total always reconciles with Fund Management.

import { dailyApi, fundApi } from "./setupApi";
import { cascadeOpening } from "./fundCascade";
import { dayAmounts } from "./consumption";
import { ensureRateCards } from "./useRateCardsSync";

const CLASS_KEYS = ["balVatika", "primary", "upperPrimary", "highSchool"];

const sumClass = (obj) =>
  CLASS_KEYS.reduce((s, k) => s + Number((obj || {})[k] || 0), 0);

/** `2026-10-07` → ISO week string like `2026-W41`. */
export function isoWeekKey(iso) {
  const [y, m, d] = iso.split("-").map((v) => parseInt(v, 10));
  const dt = new Date(Date.UTC(y, m - 1, d));
  const dayNum = dt.getUTCDay() || 7;
  dt.setUTCDate(dt.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(dt.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((dt - yearStart) / 86400000 + 1) / 7);
  return `${dt.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

/** `2026-10-07` → financial year "2026-27" (Apr→Mar). */
export function financialYearOf(iso) {
  const [y, m] = iso.split("-").map((v) => parseInt(v, 10));
  return m >= 4 ? `${y}-${String((y + 1) % 100).padStart(2, "0")}` : `${y - 1}-${String(y % 100).padStart(2, "0")}`;
}

/** Build a list of dated receipt rows for a month. Falls back to a single
 *  legacy "01-of-month" row for the full receivedRice total when the fund
 *  doc has no dated receipts yet. iter157 — also merges in stock
 *  adjustments as receipt rows with source="Stock Adjustment" so the
 *  Rice Report ledger shows reconciliation entries inline. */
export function receiptsForMonth(fundDoc) {
  if (!fundDoc) return [];
  const receipts = Array.isArray(fundDoc.riceReceipts) ? fundDoc.riceReceipts : [];
  const adjustments = (Array.isArray(fundDoc.stockAdjustments) ? fundDoc.stockAdjustments : []).map((a) => ({
    id: a.id, date: a.date, kg: Number(a.kg) || 0,
    source: "Stock Adjustment",
    refNo: a.reason || "",
    note: a.note || "",
    adjustment: true,
  }));
  if (receipts.length > 0) {
    return [...receipts.map((r) => ({ ...r, kg: Number(r.kg) || 0 })), ...adjustments];
  }
  const total = sumClass(fundDoc.receivedRice) - adjustments.reduce((s, a) => s + a.kg, 0);
  if (total <= 0 && adjustments.length === 0) return [];
  const out = [];
  if (total > 0) {
    const legacyDate = `${fundDoc.year}-${String(fundDoc.month).padStart(2, "0")}-01`;
    out.push({
      id: `legacy-${fundDoc.year}-${fundDoc.month}`,
      date: legacyDate, kg: total,
      source: "Legacy monthly total",
      refNo: "", note: "Legacy monthly total — split into real dates for a dated ledger.",
      legacy: true,
    });
  }
  return [...out, ...adjustments];
}

/** Build the daily consumption series for a month: date → rice kg consumed. */
export async function dailyRiceConsumption(schoolId, year, month) {
  const last = new Date(year, month, 0).getDate();
  const fromDate = `${year}-${String(month).padStart(2, "0")}-01`;
  const toDate = `${year}-${String(month).padStart(2, "0")}-${String(last).padStart(2, "0")}`;
  const [entries] = await Promise.all([dailyApi.list({ schoolId, fromDate, toDate }).catch(() => []), ensureRateCards()]);
  const perDate = new Map();
  entries.forEach((e) => {
    const d = dayAmounts(e);
    if (!d) return;
    const kg = sumClass(d.rice);
    if (kg <= 0) return;
    const cur = perDate.get(e.date) || { kg: 0, drKg: 0 };
    cur.kg += kg; if (d.isDR) cur.drKg += kg;
    perDate.set(e.date, cur);
  });
  return perDate;
}

/** Daily running ledger for a month. */
export async function buildDailyLedger(schoolId, year, month) {
  const fund = await fundApi.get(schoolId, year, month).catch(() => null);
  const receipts = receiptsForMonth(fund);
  const consumed = await dailyRiceConsumption(schoolId, year, month);
  // Opening = shared cascade (anchor stored opening, else previous month's closing).
  const cache = new Map();
  const op = await cascadeOpening(schoolId, year, month, cache);
  let running = op ? sumClass(op.rice) : 0;
  const openingRunning = running;
  const last = new Date(year, month, 0).getDate();
  const dates = Array.from({ length: last }, (_, i) =>
    `${year}-${String(month).padStart(2, "0")}-${String(i + 1).padStart(2, "0")}`,
  );
  const rows = dates.map((d) => {
    const inKg = receipts.filter((r) => r.date === d).reduce((s, r) => s + Number(r.kg || 0), 0);
    const outKg = Number(consumed.get(d)?.kg || 0);
    const drKg = Number(consumed.get(d)?.drKg || 0);
    running = running + inKg - outKg;
    return {
      date: d,
      in: inKg,
      inReceipts: receipts.filter((r) => r.date === d),
      out: outKg,
      drOut: drKg,
      running: Math.round(running * 1000) / 1000,
    };
  });
  const totalIn = rows.reduce((s, r) => s + r.in, 0);
  const totalOut = rows.reduce((s, r) => s + r.out, 0);
  const totalDr = rows.reduce((s, r) => s + r.drOut, 0);
  return {
    opening: openingRunning,
    rows,
    totals: { in: totalIn, out: totalOut, drOut: totalDr, closing: openingRunning + totalIn - totalOut },
    receipts,
    fund,
    hasLegacyLumpSum: receipts.some((r) => r.legacy),
    receivedRiceTotal: sumClass(fund?.receivedRice),
  };
}

/** Aggregate the daily ledger into weekly / monthly / yearly series. */
export function aggregate(rows, bucket) {
  const map = new Map();
  rows.forEach((r) => {
    let key;
    if (bucket === "week") key = isoWeekKey(r.date);
    else if (bucket === "month") key = r.date.slice(0, 7);
    else if (bucket === "year") key = financialYearOf(r.date);
    else key = r.date;
    const cur = map.get(key) || { key, in: 0, out: 0, firstDate: r.date, lastDate: r.date };
    cur.in += r.in;
    cur.out += r.out;
    if (r.date < cur.firstDate) cur.firstDate = r.date;
    if (r.date > cur.lastDate) cur.lastDate = r.date;
    cur.closingAtEnd = r.running;
    map.set(key, cur);
  });
  return [...map.values()].sort((a, b) => a.firstDate.localeCompare(b.firstDate));
}

/** Build a monthly ledger over N months (for monthly + yearly views). */
export async function buildMonthlyLedger(schoolId, fromYear, fromMonth, toYear, toMonth) {
  const rows = [];
  let y = fromYear, m = fromMonth;
  while (y < toYear || (y === toYear && m <= toMonth)) {
    // eslint-disable-next-line no-await-in-loop
    const ld = await buildDailyLedger(schoolId, y, m);
    rows.push({
      year: y, month: m,
      opening: ld.opening,
      in: ld.totals.in,
      out: ld.totals.out,
      drOut: ld.totals.drOut,
      closing: ld.totals.closing,
      hasLegacy: ld.hasLegacyLumpSum,
    });
    m += 1; if (m > 12) { m = 1; y += 1; }
  }
  return rows;
}
